import { useState } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';

export type DenominationStatus = 'pending' | 'verified' | 'rejected';
export interface ChurchDenomination {
  key: string;
  name: string;
  status: DenominationStatus;
  requestedAt: string;
  decidedAt: string | null;
  note: string | null;
}
export interface DenominationInfo {
  current: ChurchDenomination | null;
  options: { key: string; name: string }[];
}

export const DENOMINATION_STATUS: Record<DenominationStatus, string> = {
  pending: 'Pendiente de verificación',
  verified: 'Verificada',
  rejected: 'No verificada',
};

/** La denominación de la iglesia activa (se consulta una vez por iglesia). */
const cache = new Map<string, Promise<DenominationInfo>>();
export function resetDenominationCache() {
  cache.clear();
}
export function load(churchId: string): Promise<DenominationInfo> {
  if (!cache.has(churchId)) {
    const p = api.get<DenominationInfo>(`/multichurch/churches/${churchId}/denomination`).catch((e) => {
      cache.delete(churchId);
      throw e;
    });
    cache.set(churchId, p);
  }
  return cache.get(churchId)!;
}

/** ¿La persona es presbítero, superintendente o secretario de alguna sección o distrito? (una vez por sesión) */
let overseer: Promise<boolean> | null = null;
export function useIsOverseer(personId: string): boolean {
  const r = useAsync(() => {
    overseer ??= api.get<{ isOverseer: boolean }>('/multichurch/ad/overseer/me').then((x) => x.isOverseer).catch(() => false);
    return overseer;
  }, [personId]);
  return r.status === 'ready' && r.data;
}
export function resetOverseerCache() {
  overseer = null;
}

/** ¿La iglesia tiene verificada la denominación? Mientras carga o si falla, no. */
export function useVerifiedDenomination(churchId: string | null): string | null {
  const info = useAsync(() => (churchId ? load(churchId).catch(() => null) : Promise.resolve(null)), [churchId]);
  return info.status === 'ready' && info.data?.current?.status === 'verified' ? info.data.current.key : null;
}

/** En Configuración de la iglesia: la iglesia elige su denominación y pide que KEORA la verifique. */
export function DenominationCard({ churchId }: { churchId: string }) {
  const info = useAsync(() => load(churchId), [churchId]);
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  if (info.status !== 'ready') return null;
  const { current, options } = info.data;

  async function run(action: () => Promise<unknown>, ok: string) {
    setBusy(true);
    setMsg(null);
    try {
      await action();
      cache.delete(churchId);
      setMsg({ tone: 'success', text: ok });
      info.reload();
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Denominación">
      <p className="small muted">
        Si tu iglesia pertenece a una denominación, elígela. La administración de KEORA la verifica y, ya verificada, se activan las herramientas propias de
        tu denominación.
      </p>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      {current ? (
        <>
          <p>
            <strong>{current.name}</strong>{' '}
            <span className={`badge ${current.status === 'verified' ? 'badge--active' : current.status === 'pending' ? 'badge--pending' : 'badge--warn'}`}>
              {DENOMINATION_STATUS[current.status]}
            </span>
          </p>
          {current.note ? <p className="small">Nota de KEORA: {current.note}</p> : null}
          {current.status === 'pending' ? <p className="small muted">Te avisaremos cuando la verifiquemos.</p> : null}
          <div className="actions actions--wrap">
            {current.status === 'rejected' ? (
              <Button busy={busy} onClick={() => void run(() => api.put(`/multichurch/churches/${churchId}/denomination`, { key: current.key }), 'Solicitud enviada otra vez.')}>
                Solicitar de nuevo
              </Button>
            ) : null}
            <Button variant="ghost" busy={busy} onClick={() => { if (window.confirm(`Se ocultarán las herramientas de ${current.name}. ¿Quitarla?`)) void run(() => api.delete(`/multichurch/churches/${churchId}/denomination`), 'Denominación quitada.'); }}>
              Quitar denominación
            </Button>
          </div>
        </>
      ) : (
        <div className="form form--inline">
          <div className="field">
            <label htmlFor="denomination-key">Mi iglesia pertenece a</label>
            <select id="denomination-key" value={key} onChange={(e) => setKey(e.target.value)}>
              <option value="">Elige…</option>
              {options.map((o) => (
                <option key={o.key} value={o.key}>
                  {o.name}
                </option>
              ))}
            </select>
          </div>
          <Button disabled={!key} busy={busy} onClick={() => void run(() => api.put(`/multichurch/churches/${churchId}/denomination`, { key }), 'Solicitud enviada. La administración de KEORA la revisará.')}>
            Solicitar
          </Button>
        </div>
      )}
      <p className="small muted">¿Tu denominación no aparece? Escríbenos y la agregamos.</p>
    </Card>
  );
}

/** Panel KEORA: verificar o rechazar la denominación que pidió una iglesia. */
export function AdminDenominationCard({ churchId }: { churchId: string }) {
  const data = useAsync((signal) => api.get<{ items: Record<string, ChurchDenomination> }>('/multichurch/admin/denominations', signal).then((r) => r.items[churchId] ?? null), [churchId]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (data.status !== 'ready') return null;
  const d = data.data;

  async function decide(decision: 'verify' | 'reject') {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/multichurch/admin/churches/${churchId}/denomination/${decision}`, { note: note.trim() || null });
      resetDenominationCache();
      data.reload();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Denominación">
      {!d ? (
        <p className="muted">Esta iglesia no ha solicitado denominación.</p>
      ) : (
        <>
          {error ? <Alert>{error}</Alert> : null}
          <p>
            <strong>{d.name}</strong>{' '}
            <span className={`badge ${d.status === 'verified' ? 'badge--active' : d.status === 'pending' ? 'badge--pending' : 'badge--warn'}`}>{DENOMINATION_STATUS[d.status]}</span>
          </p>
          <p className="small muted">Solicitada el {new Date(d.requestedAt).toLocaleDateString('es')}</p>
          {d.note ? <p className="small">Nota: {d.note}</p> : null}
          <div className="field">
            <label htmlFor="denomination-note">Nota para la iglesia (opcional)</label>
            <input id="denomination-note" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ej. Confirmado con el distrito" />
          </div>
          <div className="actions actions--wrap">
            {d.status !== 'verified' ? (
              <Button busy={busy} onClick={() => void decide('verify')}>
                Verificar
              </Button>
            ) : null}
            {d.status !== 'rejected' ? (
              <Button variant="ghost" busy={busy} onClick={() => void decide('reject')}>
                {d.status === 'verified' ? 'Quitar verificación' : 'Rechazar'}
              </Button>
            ) : null}
          </div>
          <p className="small muted">Solo con la denominación verificada la iglesia ve las herramientas de su denominación.</p>
        </>
      )}
    </Card>
  );
}
